import type { Theme } from './theme.types';

/**
 * Темы ПЕРВОГО ЭКРАНА покупателя (WEB-3447, заход 3, решение владельца
 * 17.10 ~07:58). Полный каталог для кабинета и сторожей — `THEMES` в
 * `themeCatalog.ts`; тема с градиентами бренда («Blue Gradient») едет
 * ленивым куском плеера (`LAZY_THEMES` там же), здесь её нет.
 */
export const EAGER_THEMES: Theme[] = [
  // ── Minimalist (5) ────────────────────────────────────────────────────────
  {
    id: 'minimalist-light',
    name: 'Minimalist Light',
    category: 'minimalist',
    colors: {
      primary: '#1a1a1a',
      primaryHover: '#333333',
      background: '#ffffff',
      surface: '#fafafa',
      textPrimary: '#1a1a1a',
      textSecondary: '#6b6b6b',
      border: '#e5e5e5',
      success: '#16a34a',
      danger: '#dc2626',
      accent: '#f5f5f5',
    },
    typography: {
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 600,
    },
    radius: { sm: 2, md: 4, lg: 6, button: 4 },
    buttonStyle: 'outlined',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'minimalist-dark',
    name: 'Minimalist Dark',
    category: 'minimalist',
    colors: {
      primary: '#f0f0f0',
      primaryHover: '#ffffff',
      background: '#0f0f0f',
      surface: '#1a1a1a',
      textPrimary: '#f0f0f0',
      textSecondary: '#a0a0a0',
      border: '#2a2a2a',
      success: '#4ade80',
      danger: '#f87171',
      accent: '#252525',
    },
    typography: {
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 600,
    },
    radius: { sm: 2, md: 4, lg: 6, button: 4 },
    buttonStyle: 'outlined',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'mono-grey',
    name: 'Mono Grey',
    category: 'minimalist',
    colors: {
      primary: '#525252',
      primaryHover: '#404040',
      background: '#f5f5f5',
      surface: '#ffffff',
      textPrimary: '#262626',
      textSecondary: '#737373',
      border: '#d4d4d4',
      success: '#65a30d',
      danger: '#dc2626',
      accent: '#e5e5e5',
    },
    typography: {
      fontFamily: '"DM Sans", system-ui, sans-serif',
      fontSizeBase: 15,
      fontWeightHeading: 700,
    },
    radius: { sm: 4, md: 8, lg: 12, button: 8 },
    buttonStyle: 'solid',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'paper-white',
    name: 'Paper White',
    category: 'minimalist',
    colors: {
      primary: '#2d2d2d',
      primaryHover: '#1a1a1a',
      background: '#fffef9',
      surface: '#faf9f4',
      textPrimary: '#1c1c1c',
      textSecondary: '#757575',
      border: '#e8e6de',
      success: '#2e7d32',
      danger: '#c62828',
      accent: '#f0ede3',
    },
    typography: {
      fontFamily: '"Georgia", "Times New Roman", serif',
      headingFontFamily: '"Playfair Display", Georgia, serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 0, md: 2, lg: 4, button: 2 },
    buttonStyle: 'outlined',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'soft-cream',
    name: 'Soft Cream',
    category: 'minimalist',
    colors: {
      primary: '#9b7653',
      primaryHover: '#7d5e40',
      background: '#fdf6ee',
      surface: '#fff9f2',
      textPrimary: '#3d2b1f',
      textSecondary: '#8d6e63',
      border: '#e8d5c4',
      success: '#558b2f',
      danger: '#bf360c',
      accent: '#f5e6d8',
    },
    typography: {
      fontFamily: '"Lato", -apple-system, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 600,
    },
    radius: { sm: 4, md: 8, lg: 16, button: 8 },
    buttonStyle: 'solid',
    backgroundStyle: { type: 'solid' },
  },

  // ── Bold (5) ───────────────────────────────────────────────────────────────
  {
    id: 'vibrant-orange',
    name: 'Vibrant Orange',
    category: 'bold',
    colors: {
      primary: '#ff6b00',
      primaryHover: '#e55a00',
      background: '#fff8f4',
      surface: '#ffffff',
      textPrimary: '#1a0a00',
      textSecondary: '#6b3a1f',
      border: '#ffd4b8',
      success: '#00b894',
      danger: '#d63031',
      accent: '#ffeedd',
    },
    typography: {
      fontFamily: '"Montserrat", system-ui, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 800,
    },
    radius: { sm: 4, md: 8, lg: 16, button: 8 },
    buttonStyle: 'solid',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'neon-pink',
    name: 'Neon Pink',
    category: 'bold',
    colors: {
      primary: '#ff2d78',
      primaryHover: '#e0005c',
      background: '#0d0014',
      surface: '#1a0020',
      textPrimary: '#ffffff',
      textSecondary: '#cc99cc',
      border: '#4a004a',
      success: '#39ff14',
      danger: '#ff4444',
      accent: '#ff00aa',
    },
    typography: {
      fontFamily: '"Rajdhani", "Oswald", system-ui, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 0, md: 4, lg: 8, button: 0 },
    buttonStyle: 'gradient',
    backgroundStyle: {
      type: 'gradient',
      gradient: { from: '#0d0014', to: '#1a0030', angle: 135 },
    },
  },
  {
    id: 'electric-blue',
    name: 'Electric Blue',
    category: 'bold',
    colors: {
      primary: '#0066ff',
      primaryHover: '#0052cc',
      background: '#f0f4ff',
      surface: '#ffffff',
      textPrimary: '#001a4d',
      textSecondary: '#3355aa',
      border: '#b3c6ff',
      success: '#00cc66',
      danger: '#ff3333',
      accent: '#e0eaff',
    },
    typography: {
      fontFamily: '"Roboto", system-ui, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 4, md: 8, lg: 12, button: 8 },
    buttonStyle: 'solid',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'fire-red',
    name: 'Fire Red',
    category: 'bold',
    colors: {
      primary: '#cc0000',
      primaryHover: '#aa0000',
      background: '#fff5f5',
      surface: '#ffffff',
      textPrimary: '#1a0000',
      textSecondary: '#660000',
      border: '#ffb3b3',
      success: '#00aa44',
      danger: '#880000',
      accent: '#ffe0e0',
    },
    typography: {
      fontFamily: '"Oswald", "Roboto Condensed", system-ui, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 800,
    },
    radius: { sm: 0, md: 4, lg: 8, button: 4 },
    buttonStyle: 'solid',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'vibrant-purple',
    name: 'Vibrant Purple',
    category: 'bold',
    colors: {
      primary: '#7c00ff',
      primaryHover: '#6200cc',
      background: '#f8f0ff',
      surface: '#ffffff',
      textPrimary: '#1a0033',
      textSecondary: '#5500aa',
      border: '#ddb3ff',
      success: '#00cc88',
      danger: '#ff3355',
      accent: '#eed6ff',
    },
    typography: {
      fontFamily: '"Poppins", system-ui, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 800,
    },
    radius: { sm: 8, md: 16, lg: 24, button: 24 },
    buttonStyle: 'gradient',
    backgroundStyle: {
      type: 'gradient',
      gradient: { from: '#f8f0ff', to: '#fff0f8', angle: 145 },
    },
  },

  // ── Elegant (4) ────────────────────────────────────────────────────────────
  {
    id: 'luxury-gold',
    name: 'Luxury Gold',
    category: 'elegant',
    colors: {
      primary: '#c9a84c',
      primaryHover: '#a88530',
      background: '#0a0800',
      surface: '#1a1500',
      textPrimary: '#f5e6b8',
      textSecondary: '#c9a84c',
      border: '#3d3010',
      success: '#4caf50',
      danger: '#f44336',
      accent: '#2a2000',
    },
    typography: {
      fontFamily: '"Cormorant Garamond", "EB Garamond", Georgia, serif',
      headingFontFamily: '"Cormorant Garamond", Georgia, serif',
      fontSizeBase: 17,
      fontWeightHeading: 600,
    },
    radius: { sm: 0, md: 2, lg: 4, button: 2 },
    buttonStyle: 'outlined',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'royal-purple',
    name: 'Royal Purple',
    category: 'elegant',
    colors: {
      primary: '#6b21a8',
      primaryHover: '#581c87',
      background: '#faf5ff',
      surface: '#ffffff',
      textPrimary: '#1e0a2e',
      textSecondary: '#7e22ce',
      border: '#e9d5ff',
      success: '#15803d',
      danger: '#b91c1c',
      accent: '#f3e8ff',
    },
    typography: {
      fontFamily: '"Libre Baskerville", Georgia, serif',
      headingFontFamily: '"Cinzel", Georgia, serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 2, md: 6, lg: 10, button: 6 },
    buttonStyle: 'solid',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'midnight-emerald',
    name: 'Midnight Emerald',
    category: 'elegant',
    colors: {
      primary: '#10b981',
      primaryHover: '#059669',
      background: '#021a12',
      surface: '#041f17',
      textPrimary: '#d1fae5',
      textSecondary: '#6ee7b7',
      border: '#064e3b',
      success: '#34d399',
      danger: '#f87171',
      accent: '#065f46',
    },
    typography: {
      fontFamily: '"Crimson Text", Georgia, serif',
      fontSizeBase: 17,
      fontWeightHeading: 600,
    },
    radius: { sm: 2, md: 4, lg: 8, button: 4 },
    buttonStyle: 'outlined',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'rose-blush',
    name: 'Rose Blush',
    category: 'elegant',
    colors: {
      primary: '#be185d',
      primaryHover: '#9d174d',
      background: '#fff1f6',
      surface: '#ffffff',
      textPrimary: '#1a0010',
      textSecondary: '#9d174d',
      border: '#fbcfe8',
      success: '#15803d',
      danger: '#b91c1c',
      accent: '#fce7f3',
    },
    typography: {
      fontFamily: '"Lora", Georgia, serif',
      headingFontFamily: '"Cormorant Garamond", Georgia, serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 4, md: 10, lg: 20, button: 20 },
    buttonStyle: 'pill',
    backgroundStyle: { type: 'solid' },
  },

  // ── Playful (3) ────────────────────────────────────────────────────────────
  {
    id: 'candy-pop',
    name: 'Candy Pop',
    category: 'playful',
    colors: {
      primary: '#ff6eb4',
      primaryHover: '#ff4da6',
      background: '#fff0f9',
      surface: '#ffffff',
      textPrimary: '#330022',
      textSecondary: '#cc44aa',
      border: '#ffccee',
      success: '#00dd88',
      danger: '#ff4444',
      accent: '#ffe0f5',
    },
    typography: {
      fontFamily: '"Nunito", system-ui, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 800,
    },
    radius: { sm: 8, md: 16, lg: 24, button: 24 },
    buttonStyle: 'pill',
    backgroundStyle: {
      type: 'gradient',
      gradient: { from: '#fff0f9', to: '#f0f0ff', angle: 120 },
    },
  },
  {
    id: 'kid-friendly',
    name: 'Kid Friendly',
    category: 'playful',
    colors: {
      primary: '#ff8c00',
      primaryHover: '#e67e00',
      background: '#fffde7',
      surface: '#ffffff',
      textPrimary: '#1a1200',
      textSecondary: '#795548',
      border: '#ffe082',
      success: '#4caf50',
      danger: '#f44336',
      accent: '#fff3e0',
    },
    typography: {
      fontFamily: '"Fredoka One", "Nunito", system-ui, sans-serif',
      fontSizeBase: 17,
      fontWeightHeading: 700,
    },
    radius: { sm: 8, md: 16, lg: 32, button: 32 },
    buttonStyle: 'pill',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'retro-80s',
    name: 'Retro 80s',
    category: 'playful',
    colors: {
      primary: '#ff00ff',
      primaryHover: '#cc00cc',
      background: '#0a0020',
      surface: '#150030',
      textPrimary: '#00ffff',
      textSecondary: '#ff88ff',
      border: '#330066',
      success: '#00ff88',
      danger: '#ff0055',
      accent: '#ff00aa',
    },
    typography: {
      fontFamily: '"Orbitron", "Courier New", monospace',
      fontSizeBase: 14,
      fontWeightHeading: 700,
    },
    radius: { sm: 0, md: 0, lg: 0, button: 0 },
    buttonStyle: 'outlined',
    backgroundStyle: {
      type: 'gradient',
      gradient: { from: '#0a0020', to: '#200040', angle: 180 },
    },
  },

  // ── Corporate (3) ──────────────────────────────────────────────────────────
  {
    id: 'trust-blue',
    name: 'Trust Blue',
    category: 'corporate',
    colors: {
      primary: '#1565c0',
      primaryHover: '#0d47a1',
      background: '#f8faff',
      surface: '#ffffff',
      textPrimary: '#0d1421',
      textSecondary: '#37474f',
      border: '#c5cae9',
      success: '#2e7d32',
      danger: '#c62828',
      accent: '#e8eaf6',
    },
    typography: {
      fontFamily: '"Source Sans Pro", system-ui, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 600,
    },
    radius: { sm: 2, md: 4, lg: 8, button: 4 },
    buttonStyle: 'solid',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'professional-navy',
    name: 'Professional Navy',
    category: 'corporate',
    colors: {
      primary: '#1a237e',
      primaryHover: '#0d1458',
      background: '#f5f6fa',
      surface: '#ffffff',
      textPrimary: '#0d1421',
      textSecondary: '#455a64',
      border: '#cfd8dc',
      success: '#388e3c',
      danger: '#d32f2f',
      accent: '#e8eaf6',
    },
    typography: {
      fontFamily: '"Open Sans", system-ui, sans-serif',
      fontSizeBase: 15,
      fontWeightHeading: 700,
    },
    radius: { sm: 2, md: 4, lg: 6, button: 4 },
    buttonStyle: 'solid',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'slate-pro',
    name: 'Slate Pro',
    category: 'corporate',
    colors: {
      primary: '#475569',
      primaryHover: '#334155',
      background: '#f8fafc',
      surface: '#ffffff',
      textPrimary: '#0f172a',
      textSecondary: '#64748b',
      border: '#e2e8f0',
      success: '#16a34a',
      danger: '#dc2626',
      accent: '#f1f5f9',
    },
    typography: {
      fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
      fontSizeBase: 15,
      fontWeightHeading: 600,
    },
    radius: { sm: 4, md: 6, lg: 10, button: 6 },
    buttonStyle: 'solid',
    backgroundStyle: { type: 'solid' },
  },

  // ── Health (2) ─────────────────────────────────────────────────────────────
  {
    id: 'forest-green',
    name: 'Forest Green',
    category: 'health',
    colors: {
      primary: '#2e7d32',
      primaryHover: '#1b5e20',
      background: '#f1f8e9',
      surface: '#ffffff',
      textPrimary: '#1b2b1c',
      textSecondary: '#558b2f',
      border: '#c8e6c9',
      success: '#4caf50',
      danger: '#c62828',
      accent: '#e8f5e9',
    },
    typography: {
      fontFamily: '"Nunito", system-ui, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 6, md: 12, lg: 20, button: 12 },
    buttonStyle: 'solid',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'calm-teal',
    name: 'Calm Teal',
    category: 'health',
    colors: {
      primary: '#00838f',
      primaryHover: '#006064',
      background: '#e0f7fa',
      surface: '#ffffff',
      textPrimary: '#00212a',
      textSecondary: '#00695c',
      border: '#b2ebf2',
      success: '#00897b',
      danger: '#bf360c',
      accent: '#b2dfdb',
    },
    typography: {
      fontFamily: '"Quicksand", system-ui, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 600,
    },
    radius: { sm: 8, md: 14, lg: 24, button: 24 },
    buttonStyle: 'pill',
    backgroundStyle: { type: 'solid' },
  },

  // ── Fitness (1) ────────────────────────────────────────────────────────────
  {
    id: 'energy-yellow',
    name: 'Energy Yellow',
    category: 'fitness',
    colors: {
      primary: '#f9a825',
      primaryHover: '#f57f17',
      background: '#fffde7',
      surface: '#ffffff',
      textPrimary: '#1a1200',
      textSecondary: '#6d4c00',
      border: '#ffe082',
      success: '#43a047',
      danger: '#e53935',
      accent: '#fff8e1',
    },
    typography: {
      fontFamily: '"Barlow Condensed", "Barlow", system-ui, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 800,
    },
    radius: { sm: 2, md: 4, lg: 8, button: 4 },
    buttonStyle: 'solid',
    backgroundStyle: { type: 'solid' },
  },

  // ── Education (1) ──────────────────────────────────────────────────────────
  {
    id: 'study-blue',
    name: 'Study Blue',
    category: 'education',
    colors: {
      primary: '#2196f3',
      primaryHover: '#1976d2',
      background: '#f3f8ff',
      surface: '#ffffff',
      textPrimary: '#0d1b2a',
      textSecondary: '#546e7a',
      border: '#bbdefb',
      success: '#4caf50',
      danger: '#f44336',
      accent: '#e3f2fd',
    },
    typography: {
      fontFamily: '"Merriweather", Georgia, serif',
      headingFontFamily: '"Merriweather Sans", system-ui, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 4, md: 8, lg: 12, button: 8 },
    buttonStyle: 'solid',
    backgroundStyle: { type: 'solid' },
  },

  // ── Lifestyle (1) ──────────────────────────────────────────────────────────
  {
    id: 'warm-beige',
    name: 'Warm Beige',
    category: 'lifestyle',
    colors: {
      primary: '#a0785a',
      primaryHover: '#7d5a40',
      background: '#fdf4eb',
      surface: '#ffffff',
      textPrimary: '#2c1a0e',
      textSecondary: '#8d6e63',
      border: '#e8d5c0',
      success: '#558b2f',
      danger: '#bf360c',
      accent: '#f5e6d3',
    },
    typography: {
      fontFamily: '"Josefin Sans", system-ui, sans-serif',
      headingFontFamily: '"Playfair Display", Georgia, serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 4, md: 10, lg: 20, button: 20 },
    buttonStyle: 'pill',
    backgroundStyle: { type: 'solid' },
  },

  // ── WEB-447 W2.1: genre presets (GENRE-DESIGN-SYSTEM.md §1/§2/§5) ─────────
  // Every fontFamily/headingFontFamily below uses ONLY the FONT_MAP-loadable
  // families (Inter/Poppins/Manrope/Playfair Display/Lora/Philosopher/DM Serif
  // Display, see themes/fonts.ts) — nunito/spacegrotesk are deferred (§526).
  {
    id: 'mint-care',
    name: 'Mint Care',
    category: 'health',
    // Ж1 mental_health + meditation — pastel-mint care (Sensa, grounded).
    colors: {
      primary: '#0fb5ae',
      primaryHover: '#0c9a94',
      background: '#e8f7f5',
      surface: '#ffffff',
      textPrimary: '#173d3a',
      textSecondary: '#4d7370',
      border: '#c9e9e5',
      success: '#16a34a',
      danger: '#dc2626',
      accent: '#d6f0ee',
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      headingFontFamily: '"Manrope", -apple-system, BlinkMacSystemFont, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 800,
    },
    radius: { sm: 8, md: 16, lg: 16, button: 999 },
    buttonStyle: 'pill',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'ember-dark',
    name: 'Ember Dark',
    category: 'fitness',
    // Ж2 fitness (hard/male) + weight_loss (hard) — ember-dark (MadMuscles,
    // grounded: image-tiles observed inside a live funnel).
    colors: {
      primary: '#ff7a00',
      primaryHover: '#e56d00',
      background: '#0f0f10',
      surface: '#1a1a1c',
      textPrimary: '#ffffff',
      textSecondary: '#a3a3a8',
      border: '#2a2a2c',
      success: '#4ade80',
      danger: '#f87171',
      accent: '#3a2410',
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      headingFontFamily: '"Poppins", -apple-system, BlinkMacSystemFont, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 800,
    },
    radius: { sm: 6, md: 12, lg: 12, button: 999 },
    buttonStyle: 'pill',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'cream-editorial',
    name: 'Cream Editorial',
    category: 'lifestyle',
    // Ж3 nutrition + health + weight_loss (soft) — cream-editorial
    // (Noom/Zoe/Oura/Flo, grounded prelander).
    colors: {
      primary: '#ff5a5f',
      primaryHover: '#e84e53',
      background: '#f2f0e9',
      surface: '#fffdf8',
      textPrimary: '#26221a',
      textSecondary: '#6f6a5e',
      border: '#e8e4d8',
      success: '#558b2f',
      danger: '#bf360c',
      accent: '#ffe9ea',
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      headingFontFamily: '"Lora", Georgia, serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 8, md: 14, lg: 14, button: 999 },
    buttonStyle: 'pill',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'navy-mystic',
    name: 'Navy Mystic',
    category: 'elegant',
    // Ж4 astrology — navy-mystic (Nebula, grounded prelander).
    // §1 inverted cream pill (WEB-447 §4.3(d)): the CTA fill is a warm cream
    // (#f5efe0) with dark ink text, deliberately distinct from the periwinkle
    // page accent (#8b8bf5). Wired via buttonFill/onPrimary → --color-button-
    // fill / --color-on-primary (themeToStyle); primaryButtonStyle's pill reads
    // --color-button-fill with a fallback to --color-primary for all other themes.
    colors: {
      primary: '#8b8bf5',
      primaryHover: '#7373e8',
      background: '#12131a',
      surface: '#1c1d26',
      textPrimary: '#f2efe6',
      textSecondary: '#9a97b0',
      border: '#2a2b38',
      success: '#4ade80',
      danger: '#f87171',
      accent: '#23243a',
      buttonFill: '#f5efe0',
      onPrimary: '#12131a',
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      headingFontFamily: '"Philosopher", "Open Sans", sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 8, md: 16, lg: 16, button: 999 },
    buttonStyle: 'pill',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'blush-pastel',
    name: 'Blush Pastel',
    category: 'playful',
    // Ж5 parenting + relationships — blush-pastel (welcome.baby/Paired,
    // grounded prelander).
    colors: {
      primary: '#f27e9d',
      primaryHover: '#e06a8b',
      background: '#f9f1f3',
      surface: '#ffffff',
      textPrimary: '#3a2c33',
      textSecondary: '#8a7580',
      border: '#f6dde4',
      success: '#16a34a',
      danger: '#dc2626',
      accent: '#fde4ea',
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      headingFontFamily: '"Poppins", -apple-system, BlinkMacSystemFont, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 10, md: 20, lg: 20, button: 999 },
    buttonStyle: 'pill',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'sage-editorial',
    name: 'Sage Editorial',
    category: 'elegant',
    // Ж6 ecommerce + style — sage-editorial (Prose/Stitch Fix, grounded
    // prelander). Buttons are ink-dark, NOT a colorful accent (genre trait).
    colors: {
      primary: '#1f2937',
      primaryHover: '#111827',
      background: '#eef0e9',
      surface: '#ffffff',
      textPrimary: '#1f2937',
      textSecondary: '#6b7280',
      border: '#dde1d4',
      success: '#16a34a',
      danger: '#dc2626',
      accent: '#7a8b6f',
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      headingFontFamily: '"Playfair Display", Georgia, serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 8, md: 16, lg: 16, button: 999 },
    buttonStyle: 'pill',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'bento-brutalist',
    name: 'Bento Brutalist',
    category: 'education',
    // Ж9 education + language_learning + productivity — bento-brutalist
    // (Promova/Brilliant/Motion, grounded prelander). Forces the new
    // 'brutalist' buttonStyle into a real render branch (see themes/index.ts
    // primaryButtonStyle).
    // NOTE (deviation): §1 surface = the bento tiles themselves, each a
    // different pastel (peach/sky/lilac/mint) — Theme.colors.surface is a
    // single hex, so a multi-tile bento grid can't be represented as one
    // "surface" swatch. Using the peach tile as the representative surface;
    // the sky tile is carried in `accent` as a secondary tint.
    colors: {
      primary: '#111111',
      primaryHover: '#2a2a2a',
      background: '#fdf9f3',
      surface: '#ffe9d6',
      textPrimary: '#111111',
      textSecondary: '#5f5b54',
      border: '#f0d9c0',
      success: '#16a34a',
      danger: '#dc2626',
      accent: '#e5f0ff',
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      headingFontFamily: '"Poppins", -apple-system, BlinkMacSystemFont, sans-serif',
      fontSizeBase: 16,
      fontWeightHeading: 800,
    },
    radius: { sm: 12, md: 16, lg: 24, button: 12 },
    buttonStyle: 'brutalist',
    backgroundStyle: { type: 'solid' },
  },
  {
    id: 'ink-premium',
    name: 'Ink Premium',
    category: 'elegant',
    // Ж10 self_improvement + religion — ink-premium (Hallow/Fabulous/Blinkist,
    // grounded prelander).
    // NOTE (deviation): §1 surface carries jewel-tone card inserts (emerald
    // #14532d / burgundy #7f1d1d / navy #1e3a5f) distinct from the #ffffff
    // page surface — a single-hex Theme.colors.accent can't hold all three;
    // using the emerald as the representative jewel-tone accent.
    colors: {
      primary: '#111111',
      primaryHover: '#2a2a2a',
      background: '#fdfcfa',
      surface: '#ffffff',
      textPrimary: '#161616',
      textSecondary: '#6b6b6b',
      border: '#e8e6e0',
      success: '#16a34a',
      danger: '#dc2626',
      accent: '#14532d',
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      headingFontFamily: '"DM Serif Display", Georgia, serif',
      fontSizeBase: 16,
      fontWeightHeading: 700,
    },
    radius: { sm: 8, md: 16, lg: 16, button: 999 },
    buttonStyle: 'pill',
    backgroundStyle: { type: 'solid' },
  },
];
